import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// MUI Components
import {
  Alert,
  Box,
  Button,
  Grid2 as Grid,
  IconButton,
  Snackbar,
  styled,
  Typography,
} from '@mui/material';

// MUI Icons
import {
  Abc as AbcIcon,
  CloudUpload,
  CloudDownload,
  CalendarMonth as CalendarMonthIcon,
  Send as SendIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Code as CodeIcon,
  AttachMoney as AttachMoneyIcon,
  Label as LabelIcon,
  Image as ImageIcon,
  Source as SourceIcon,
  Note as NoteIcon,
  Tag as TagIcon,
  Launch as LaunchIcon,
  QrCode as QrCodeIcon
} from "@mui/icons-material";

// Contexts
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';

// External Libraries
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

// Utilities & Helpers
import {
  addToSentItemsLS,
  calcRefFees,
  calcWfs,
  cleanItemMetadataToPreventQuotaExceeded,
  fetchNextDataWithRetry,
  fetchWrapper,
  fetchWrapperNoAuth,
  mergeObjects,
  sendErrorMessage,
  SERVER
} from '../../utils/helpers';
import {
  checkFeed,
  checkItem,
  convertItemWFS,
  MAX_STEP_RETRIES
} from '../../utils/proccessHelpers';

// Components
import Form from '../../components/ProcessComponents/Form';
import FormSF from '../../components/ProcessComponents/FormSF';
import CustomStepper from '../../components/ProcessComponents/CustomStepper';
import ResizableSidebar from '../../components/SideBar/ResizableSidebar';
import ResponsiveItemCard from '../../components/SideBar/ResponsiveItemCard';

// Modals
import BatchSelectionModal from '../../components/Modals/BatchSelectionModal';
import BarcodeLabelModal from '../../components/Modals/BarcodeLabelModal';
import ErrorModal from '../../components/Modals/ErrorModal';
import NewBatchModal from '../../components/Modals/NewBatchModal';
import QuestionModal from '../../components/Modals/QuestionModal';
import ShippmentsModal from '../../components/Modals/ShippmentsModal';
import BarcodeMultipleLabelsModal from '../../components/Modals/BarcodeMultipleLabelsModal';

// Tables
import SearchedProductsTable from '../../components/Tables/SearchedProductsTable';

// Tooltips & Loaders
import HtmlTooltip from '../../components/Tooltip/HtmlTooltip';
import CustomLinearProgress from '../../components/Loaders/CustomLinearProgress';
import CustomCircularProgress from '../../components/Loaders/CustomCircularProgress';
import SearchCatalogModal from '../../components/Modals/SearchCatalogModal';


const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const STORAGE_KEY_ADDED_ITEMS = 'IFCSVUploadProcess_addedItems';
const STORAGE_KEY_CSV_ITEMS = 'IFCSVUploadProcess_csvItems';
const STORAGE_KEY_BATCH_INFO = 'IFCSVUploadProcess_batchInfo';
const STORAGE_KEY_ACTIVE_STEP = 'IFCSVUploadProcess_activeStep';
const STORAGE_KEY_CURR_PRODUCT = 'IFCSVUploadProcess_currProduct';
const STORAGE_KEY = "IFCsvUploadProccess";

function CsvUpload(props) {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { encodedAuth, user, password, isSubUser } = useAuth();

  const [sidebarWidth, setSidebarWidth] = useState(320);

  const fileInputRef = useRef(null);

  const [successedItems, setSuccessedItems] = useState(0);
  const [barcodeLabelProduct, setBarcodeLabelProduct] = useState({});
  const [openBarcodeModal, setOpenBarcodeModal] = useState(false);
  const [shippmentOpenModal, setShippmentOpenModal] = useState(false);
  const [openDeleteFromBatchModal, setOpenDeleteFromBatchModal] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [itemToModify, setItemToModify] = useState({});
  const [openErrorModal, setopenErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [alertBatch, setAlertBatch] = useState({ open: false, severity: "info", message: "" });
  const [identifier, setIdentifier] = useState("");

  const [batchesArray, setBatchesArray] = useState([]);
  const [addedItems, setAddedItems] = useState([]);
  const [csvItems, setCsvItems] = useState([]);
  const [channel, setChannel] = useState('wfs');
  const [batchName, setBatchName] = useState("My CSV");
  const [activeStep, setActiveStep] = useState(1);
  const [currProduct, setCurrProduct] = useState(null);
  const [loadStates, setLoadStates] = useState(false);
  const [skuUpdate, setSkuUpdate] = useState(false);
  const [openSearchCatalog, setOpenSearchCatalog] = useState(false);
  const [isReplen, setIsReplen] = useState(false);

  const [openMultipleBarcodeModal, setOpenMultipleBarcodeModal] = useState(false);
  const [loadingBarcode, setLoadingBarcode] = useState(false);

  const [newBatchOpen, setNewBatchOpen] = useState(batchName === "");
  const steps = ['Select Batch Name', 'Upload CSV', 'Select from CSV', 'Fill Product details'];

  const [prefetchStatus, setPrefetchStatus] = useState({});
  const [isLoadingUploadFile, setIsLoadingUploadFile] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [scrapeLoading, setScrapeLoading] = useState(false);

  // Single useEffect to handle all localStorage updates
  useEffect(() => {
    if (batchName === "My CSV") {
      return;
    }
    if (loadStates) {
      const storage = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
      storage[batchName] = {
        addedItems: addedItems.map(cleanItemMetadataToPreventQuotaExceeded),
        csvItems: csvItems,
        channel: channel,
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(storage));
    }
  }, [loadStates, addedItems, csvItems, channel]);

  const loadStatesFromLocalStorage = (batchSelected) => {
    try {
      const storage = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
      setAddedItems((storage[batchSelected].addedItems || []).map(cleanItemMetadataToPreventQuotaExceeded));
      setSuccessedItems((storage[batchSelected].addedItems || []).filter(item => item.status === "SUCCESS").length);
      setCsvItems(storage[batchSelected].csvItems || []);
      setChannel(storage[batchSelected].channel || "wfs");
      setBatchName(batchSelected || "My CSV");
      setActiveStep(2);
      setLoadStates(true);
      setOpenConfirmModal(false);
    } catch (error) {
      console.error("Failed to load states from localStorage", error);
      // Ensure default values if an error occurs
      setAddedItems([]);
      setSuccessedItems(0);
      setCsvItems([]);
      setChannel("wfs");
      setBatchName("My CSV");
      setActiveStep(1);
      setLoadStates(true);
      setOpenConfirmModal(false);
    }
  };

  // This useEffect runs once on component mount to load from localStorage
  useEffect(() => {
    const loadPrevData = async () => {
      try {
        const oldStorage = {
          addedItems: JSON.parse(localStorage.getItem(STORAGE_KEY_ADDED_ITEMS)) || [],
          csvItems: JSON.parse(localStorage.getItem(STORAGE_KEY_CSV_ITEMS)) || [],
          batchInfo: JSON.parse(localStorage.getItem(STORAGE_KEY_BATCH_INFO)) || {},
        };
        if ((oldStorage.addedItems.length > 0 || oldStorage.csvItems.length > 0) && oldStorage.batchInfo?.batchName) {
          const newStorage = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
          newStorage[oldStorage.batchInfo?.batchName] = {
            addedItems: oldStorage.addedItems,
            csvItems: oldStorage.csvItems,
            channel: oldStorage.channel
          }
          localStorage.setItem(STORAGE_KEY, JSON.stringify(newStorage));
          localStorage.removeItem(STORAGE_KEY_ADDED_ITEMS);
          localStorage.removeItem(STORAGE_KEY_CSV_ITEMS);
          localStorage.removeItem(STORAGE_KEY_BATCH_INFO);
          localStorage.removeItem(STORAGE_KEY_ACTIVE_STEP);
          localStorage.removeItem(STORAGE_KEY_CURR_PRODUCT);
        }
      } catch (error) {
        console.error("Error checking localStorage", error);
      }
    }
    loadPrevData();

    const loadInitialState = async () => {
      try {
        const storage = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
        const batches = Object.keys(storage);
        if (batches.length > 0) {
          setBatchesArray(batches);
          setOpenConfirmModal(true);
        } else {
          setLoadStates(true); // No stored data, enable storing right away
        }

        localStorage.removeItem(STORAGE_KEY_ADDED_ITEMS);
        localStorage.removeItem(STORAGE_KEY_CSV_ITEMS);
        localStorage.removeItem(STORAGE_KEY_BATCH_INFO);
        localStorage.removeItem(STORAGE_KEY_ACTIVE_STEP);
        localStorage.removeItem(STORAGE_KEY_CURR_PRODUCT);
      } catch (error) {
        console.error("Error checking localStorage", error);
        setLoadStates(true);
      }
    };

    loadInitialState();
  }, []);

  // Clear localStorage data when items are successfully sent
  const clearStoredData = (batchSelected) => {
    const storage = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    delete storage[batchSelected];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(storage));
    setBatchesArray((batches) => batches.filter(b => b !== batchSelected));
  };

  const showAlert = (severity, message) => {
    setAlertBatch({ open: true, severity, message });

    // Auto-close after 3 seconds
    setTimeout(() => {
      setAlertBatch({ ...alertBatch, open: false });
    }, 3000);
  };

  const handleSaveBatches = async () => {
    const batches = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    if (Object.keys(batches).length > 0) {
      const body = {
        email: user,
        page: "CsvUpload",
        batches: batches
      };
      try {
        await fetchWrapperNoAuth(`${SERVER}/falcon_save_batches`, user, password, { method: "POST", body: body });
        showAlert("success", "Batches have been successfully saved!");
      } catch (e) {
        showAlert("error", "An error occurred while saving batches. Please try again.");
      }
    } else {
      showAlert("warning", "No batches found to save. Please add some batches first.");
    }
  };

  const handleLoadBatches = async () => {
    const body = {
      email: user,
      page: "CsvUpload"
    };
    try {
      const batches = await fetchWrapperNoAuth(`${SERVER}/falcon_load_batches`, user, password, { method: "POST", body: body });
      if (Object.keys(batches).length > 0) {
        setAddedItems([]);
        setSuccessedItems(0);
        setCsvItems([]);
        setChannel("wfs");
        setBatchName("My CSV");
        setActiveStep(1);
        showAlert("success", "Batches have been successfully loaded! Refresh the page to view them.");
        const cleanedBatches = {};
        Object.keys(batches).forEach(batchName => {
          cleanedBatches[batchName] = {
            ...batches[batchName],
            addedItems: (batches[batchName].addedItems || []).map(cleanItemMetadataToPreventQuotaExceeded),
            searchedItems: (batches[batchName].searchedItems || []),
            channel: batches[batchName].channel
          };
        });
        localStorage.setItem(STORAGE_KEY, JSON.stringify(cleanedBatches));
        window.location.reload();
      } else {
        showAlert("info", "No saved batches found in the cloud.");
      }
    } catch (e) {
      console.error(e);
      showAlert("error", "An error occurred while loading batches. Please try again.");
    }
  };

  const alertError = (msg) => {
    setErrorMessage(msg);
    setopenErrorModal(true);
  }

  const prefetchItemData = async (item) => {
    if (!item || prefetchStatus[item.itemId]) return;

    setPrefetchStatus(prev => ({
      ...prev,
      [item.itemId]: 'loading'
    }));

    let updatedItem = item;

    try {
      let nextData = await fetchNextDataWithRetry(user, password, item.itemId);
      if ((!nextData.length && !nextData.height && !nextData.width) || !nextData.weight || !nextData.manufacturer) {
        try {
          const azData = await fetchWrapperNoAuth(`${SERVER}/get_item_amazon_data/${item.upc || nextData.upc}`, user, password);
          if (azData.fromAz) {
            if ((!nextData.length && !nextData.height && !nextData.width) && (azData.length && azData.height && azData.width)) {
              nextData.dimsFromAz = true;
              nextData.length = azData.length;
              nextData.height = azData.height;
              nextData.width = azData.width;
            } else {
              nextData.dimsFromAz = false;
            }
            if (!nextData.weight && azData.weight) {
              nextData.weightFromAz = true;
              nextData.weight = azData.weight;
            } else {
              nextData.weightFromAz = false;
            }
            if (!nextData.manufacturer && azData.manufacturer) {
              nextData.manufacturerFromAz = true;
              nextData.manufacturer = azData.manufacturer;
            } else {
              nextData.manufacturerFromAz = false;
            }
            if (!nextData.brand && azData.brand) {
              nextData.brandFromAz = true;
              nextData.brand = azData.brand;
            } else {
              nextData.brandFromAz = false;
            }
          }
        } catch (error) {
          console.error(`Failed to prefetch data (get_amazon_bb_data) for UPC: ${item.upc}:`, error.message);
        }
      }
      updatedItem = mergeObjects(updatedItem, nextData);
    } catch (error) {
      console.error(`Failed to prefetch data (get_item_nextdata) for Item Id: ${item.itemId}:`, error.message);
    }

    const body = {
      'query': item.itemId
    };
    try {
      const items_search = await fetchWrapper(`${SERVER}/get_item_search`, user, password, encodedAuth, { method: 'POST', body: body });
      const item_search = items_search.filter((p) => p.itemId === item.itemId)[0];
      updatedItem = mergeObjects(item_search, updatedItem);
    } catch (error) {
      console.error(`Failed to prefetch data (get_item_search) for Item Id: ${item.itemId}:`, error.message);
    }

    try {
      setCsvItems(prevItems =>
        prevItems.map(prevItem =>
          prevItem.itemId === item.itemId
            ? updatedItem
            : prevItem
        )
      );

      // Update currProduct if this is the currently selected item
      setCurrProduct(prevProduct =>
        prevProduct?.itemId === item.itemId
          ? updatedItem
          : prevProduct
      );

      setPrefetchStatus(prev => ({
        ...prev,
        [item.itemId]: 'completed'
      }));
    } catch (error) {
      console.error(`Failed to prefetch data for Item Id: ${item.itemId}:`, error.message);
      setPrefetchStatus(prev => ({
        ...prev,
        [item.itemId]: 'error'
      }));
    }
  };

  const handleAddItem = (product) => {
    if (!product) return;

    setAddedItems((prevItems) => {
      const existingIndex = prevItems.findIndex((item) => item.sku === product.sku);

      if (existingIndex !== -1) {
        // Replace the existing item
        return prevItems.map((item, index) =>
          index === existingIndex ? { ...product } : item
        );
      }

      // Add new product to the beginning
      return [{ ...product }, ...prevItems];
    });

    setCsvItems((prevData) => {
      return prevData.filter((item) => {
        const identifier = product["upc"] || product["itemId"];
        const currentIdentifier = item["upc"] || item["itemId"];
        return currentIdentifier !== identifier;
      });
    });

    setCurrProduct({});
    setActiveStep(2);
  };

  const handleAddAnotherItem = () => {
    setActiveStep(2);
    setCurrProduct({});
  };

  const handleSendAllItems = () => {
    if (addedItems.length === 0) {
      alertError('First add items');
      return;
    }
    if (addedItems.every(item => item.status !== "LOADING")) {
      setShippmentOpenModal(true);
    } else {
      alertError('Please wait until all items have finished processing!');
    }
  };

  const removeFromBatch = async (item) => {
    // Remove from addedItems array
    setAddedItems((prevItems) => prevItems.filter((oldItem => oldItem.sku !== item.sku)));

    // Remove batchName from DB
    const metadata = {
      batchName: "none",
      email: user,
      productName: item.productName,
      image: item.image,
      sku: item.sku,
      gtin: item.gtin,
      upc: item.upc,
      cost: item.cost,
      qty: item.qty,
      innerPack: item.innerPack,
      source: item.source || "",
      note: item.note || "",
      expirationDate: item.expiration_date || null,
      inboundOrderId: item.inbound_order_id || "",
      shipmentId: item.shipment_id || "",
      purchaseDate: item.purchase_date || "",
      productType: item.product_type,
      schema: item.schema,
      MPItem: item.mp_item
    }
    const body = {
      sku: item.sku,
      email: user,
      batchName: "none",
      metadata: metadata
    }
    try {
      await fetchWrapperNoAuth(`${SERVER}/falcon_update_batch`, user, password, { method: 'POST', body: body });
    } catch (error) {
      console.error('Error update batch:', error.message);
    }
  };

  const editItem = (item) => {
    setCurrProduct(item);
    setActiveStep(3);
  };

  const retryAddItem = async (item) => {
    const url = `${SERVER}/send_item_feed_match`;
    const body = {
      item: {
        sku: item.sku,
        upc: item.upc,
        price: item.price.amount,
        weight: item.weight
      }
    }
    try {
      const { feedId } = await fetchWrapper(url, user, password, encodedAuth, { method: 'POST', body: body });
      // Create a new array by mapping through the current items
      const updatedItems = addedItems.map(existingItem => {
        // If the SKU matches, replace the item with the new one
        if (existingItem.sku === item.sku) {
          return {
            ...item,
            step: 1,
            feedId: feedId,
            status: "LOADING", // Reset the status
            retryCount: 0, // Increment retry count
            stepRetryCount: 0, // Increment step retry count
            lastRequest: new Date().toISOString() // Update the last request timestamp
          };
        }
        // Otherwise keep the existing item
        return existingItem;
      });

      // Update the state with the new array
      setAddedItems(updatedItems);
    } catch (error) { }
  };

  const capitalizeKeys = (obj) => {
    return Object.keys(obj).reduce((acc, key) => {
      if (key.toLocaleLowerCase().includes("barcode") || key.toLocaleLowerCase().includes("upc") || key.toLocaleLowerCase().includes("ean")) {
        acc["Barcode"] = obj[key];
        return acc;
      }
      if (key.toLocaleLowerCase().includes("item id")) {
        acc["Item ID"] = obj[key];
        return acc;
      }
      const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
      acc[capitalizedKey] = obj[key];
      return acc;
    }, {});
  };

  // CSV handle
  const handleFileButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event) => {
    const files = event.target.files;

    if (files.length > 0) {
      setIsLoadingUploadFile(true);
      uploadCSV(files[0]);
    } else {
      setIsLoadingUploadFile(false);
    }
  };

  function uploadCSV(file) {
    // const lastClickTimestamp = localStorage.getItem('IFlastClickTimestamp') || -1;
    // const minutesSinceLastClick = (Date.now() - parseInt(lastClickTimestamp)) / (1000 * 60);
    // if (minutesSinceLastClick < 2 && lastClickTimestamp !== -1) {
    //   setIsLoadingUploadFile(false);
    //   setRefresh(true);
    //   setModalMsg(`Please wait ${((2 - minutesSinceLastClick) * 60).toFixed(0)} seconds before scanning again!`);
    //   setOpenModal(true);
    //   window.location.reload();
    //   return
    // }
    if (file.size > 10000000) {
      setIsLoadingUploadFile(false);
      alertError("Your file is larger than 10MB. Please consider splitting the Excel file and sending multiple scan requests.");
    } else {
      if (file) {
        const reader = new FileReader();

        reader.onload = function (e) {
          const fileName = file.name.toLowerCase();
          setBatchName(fileName);
          if (fileName.endsWith('.csv')) {
            const text = e.target.result;
            Papa.parse(text, {
              header: true,
              complete: (results) => {
                const editData = results.data.map((item) => capitalizeKeys(item));
                if (editData.some(item => identifier in item)) {
                  localStorage.setItem('IFlastClickTimestamp', Date.now());
                  const uniqueData = Object.values(
                    editData.reduce((acc, item) => {
                      const key = item[identifier];
                      const cost = parseFloat(item['Cost Price']);

                      if (!acc[key] || cost < parseFloat(acc[key]['Cost Price'])) {
                        acc[key] = item;
                      }

                      return acc;
                    }, {})
                  );
                  sendFile(uniqueData);
                } else {
                  setIsLoadingUploadFile(false);
                  alertError(`Your .csv file does not contain a ${identifier} column. Please try again with a new file or download the template file.`);
                }
              },
            });
          } else if (fileName.endsWith('.xlsx')) {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            const headers = json[0];
            const dataRows = json.slice(1).map((row) => {
              const obj = {};
              headers.forEach((header, index) => {
                obj[header] = row[index];
              });
              return obj;
            });
            const editData = dataRows.map((item) => capitalizeKeys(item));
            if (editData.some(item => identifier in item)) {
              localStorage.setItem('IFlastClickTimestamp', Date.now());
              const uniqueData = Object.values(
                editData.reduce((acc, item) => {
                  const key = item[identifier];
                  const cost = parseFloat(item['Cost Price']);

                  if (!acc[key] || cost < parseFloat(acc[key]['Cost Price'])) {
                    acc[key] = item;
                  }

                  return acc;
                }, {})
              );
              sendFile(uniqueData);
              // sendFile(editData);
            } else {
              setIsLoadingUploadFile(false);
              alertError(`Your .xlsx file does not contain a ${identifier} column. Please try again with a new file or download the template file.`);
            }
          }
        };

        if (file.name.toLowerCase().endsWith('.csv')) {
          reader.readAsText(file);
        } else if (file.name.toLowerCase().endsWith('.xlsx')) {
          reader.readAsArrayBuffer(file);
        }
      }
    }

    const sendFile = async (data_l) => {
      // Process data_l in batches with delay
      const batchSize = 10;
      let index = 0;

      async function processBatch() {
        if (index < data_l.length) {
          setIsLoading(true);
          const batch = data_l.slice(index, index + batchSize).map(item => ({
            [identifier]: item[[identifier]],
            "Cost Price": item["Cost Price"] ? item["Cost Price"].replace("$", "") : -1,
            "Name": item["Name"],
            "Image": item["Image"],
            "Source": item["Source"],
            "SKU": item["SKU"],
            "Sell Price": item["Sell Price"] ? item["Sell Price"].replace("$", "") : -1,
            "Expiration Date": new Date(item["Expiration Date"]),
            "Purchase Date": new Date(item["Purchase Date"]),
            "Quantity": item["Quantity"],
            "Notes": item["Notes"],
          }));

          const options = {
            method: "POST",
            body: {
              csvFile: batch,
              identifier: identifier
            },
          };

          try {
            const data = await fetchWrapperNoAuth(`${SERVER}/upload_scanner_csv`, user, password, options);
            if (index === 0) {
              setCurrProduct(data[0]);
              data.slice(0, 5).forEach(item => prefetchItemData(item));
              handleNext();
            }

            // Filter data with combined csvItems & filteredData
            setCsvItems((prevData) => {
              const updatedData = [...prevData];

              data.forEach((newItem) => {
                const identifier = newItem["Barcode"] || newItem["Item ID"] || newItem["itemId"];
                const existingIndex = updatedData.findIndex(
                  (item) => (item["Barcode"] || item["Item ID"] || item["itemId"]) === identifier
                );

                if (existingIndex !== -1) {
                  // If the item exists, update only if the new cost is lower
                  if (newItem.cost < updatedData[existingIndex].cost) {
                    updatedData[existingIndex].cost = newItem.cost;
                  }
                } else {
                  // If it doesn't exist, add it
                  updatedData.push(newItem);
                }
              });

              return updatedData;
            });

            if (index >= data_l.length) {
              setIsLoading(false);
            }
            setIsLoadingUploadFile(false);
          } catch (e) {
            alertError(e.toString());
            setIsLoadingUploadFile(false);
            return;
          }

          index += batchSize;

          // Set timeout for 20 second before processing next batch
          setTimeout(processBatch, 20000); // 20000 milliseconds = 20 second
        } else {
          setIsLoading(false);
        }
      }

      // Start processing batches
      processBatch();
    }
  }

  const handleAddItemReplen = async (product) => {
    handleAddItem({ ...product, status: "UPLOAD", step: 7 });
    const body = { sku: product.sku };
    const url = `${SERVER}/get_an_item`;
    try {
      const response = await fetchWrapper(url, user, password, encodedAuth, { method: 'POST', body });
      const itemData = response.ItemResponse[0];
      if (itemData.publishedStatus === "PUBLISHED") {
        const addedItem = {
          ...product,
          availability: itemData.availability,
          condition: itemData.condition,
          gtin: itemData.gtin,
          lifecycleStatus: itemData.lifecycleStatus,
          mart: itemData.mart,
          price: itemData.price,
          productName: itemData.productName,
          title: itemData.productName,
          productType: itemData.productType,
          publishedStatus: itemData.publishedStatus,
          shelf: JSON.stringify(itemData.shelf.split("/")),
          sku: itemData.sku,
          unpublishedReasons: itemData.unpublishedReasons ?? { reason: [] },
          upc: itemData.upc,
          wpid: itemData.wpid,

          status: "SUCCESS",
          feedStatus: "PROCESSED",
          feedId: '',

          images: [],
          MPItem: product.mp_item,
          isWfs: true,
          lastRequest: new Date().toISOString(),
          step: 6
        }
        handleAddItem(addedItem);
      } else {
        setAddedItems((prevItems) => prevItems.filter((oldItem => oldItem.sku !== product.sku)));
        alertError(`Product with sku : ${product.sku} is not published!`);
        return;
      }
    } catch (e) {
      console.error(e);
      setAddedItems((prevItems) => prevItems.filter((oldItem => oldItem.sku !== product.sku)));
      return;
    }
  };

  // Steps
  const handleNext = async (product) => {
    if (activeStep === 0) {
      if (!batchName) {
        alert("Create new Batch");
        setNewBatchOpen(true);
        return;
      }
    }

    if (activeStep === 2) {
      if (!product || Object.keys(product).length === 0) {
        alertError("Please select item")
        return;
      }
      // if (!product.isMarketPlaceItem) {
      //   alertError(`Item ${product.itemId} is not marketplace item.`);
      //   return;
      // }
      setScrapeLoading(true);
      let tempProduct = product;

      // If nextData wasn't successfully prefetched, fetch it now
      if (prefetchStatus[product.itemId] !== 'completed' || !product.hasOwnProperty("length") || !product.hasOwnProperty("description")) {
        if (!product.hasOwnProperty("length")) {
          try {
            let nextData = await fetchNextDataWithRetry(user, password, product.itemId);
            if ((!nextData.length && !nextData.height && !nextData.width) || !nextData.weight || !nextData.manufacturer) {
              try {
                const azData = await fetchWrapperNoAuth(`${SERVER}/get_item_amazon_data/${product.upc || nextData.upc}`, user, password);
                if (azData.fromAz) {
                  if ((!nextData.length && !nextData.height && !nextData.width) && (azData.length && azData.height && azData.width)) {
                    nextData.dimsFromAz = true;
                    nextData.length = azData.length;
                    nextData.height = azData.height;
                    nextData.width = azData.width;
                  } else {
                    nextData.dimsFromAz = false;
                  }
                  if (!nextData.weight && azData.weight) {
                    nextData.weightFromAz = true;
                    nextData.weight = azData.weight;
                  } else {
                    nextData.weightFromAz = false;
                  }
                  if (!nextData.manufacturer && azData.manufacturer) {
                    nextData.manufacturerFromAz = true;
                    nextData.manufacturer = azData.manufacturer;
                  } else {
                    nextData.manufacturerFromAz = false;
                  }
                  if (!nextData.brand && azData.brand) {
                    nextData.brandFromAz = true;
                    nextData.brand = azData.brand;
                  } else {
                    nextData.brandFromAz = false;
                  }
                }
              } catch (error) {
                console.error(`Failed to prefetch data (get_amazon_bb_data) for UPC: ${product.upc}:`, error.message);
              }
            }
            tempProduct = mergeObjects(tempProduct, nextData);
          } catch (error) {
            console.error(`Failed to fetch from get_item_nextdata for Item Id: ${product.itemId}:`, error.message);
          }
        }
        if (!product.hasOwnProperty("description")) {
          const body = {
            'query': product.itemId
          };
          try {
            const items_search = await fetchWrapper(`${SERVER}/get_item_search`, user, password, encodedAuth, { method: 'POST', body: body });
            const item_search = items_search.filter((p) => p.itemId === product.itemId)[0];
            tempProduct = mergeObjects(item_search, tempProduct);
          } catch (error) {
            console.error(`Failed to prefetch data (get_item_search) for Item Id: ${product.itemId}:`, error.message);
          }
        }
      } else {
        // If nextData was prefetched, get the updated product from csvItems
        tempProduct = csvItems.find(item => item.itemId === product.itemId);
      }

      // Always fetch catalog search data when moving to next step
      try {
        const catalogSearch = await fetchWrapper(`${SERVER}/post_catalog_search/${product.itemId}/${user}`, user, password, encodedAuth);
        tempProduct = mergeObjects(catalogSearch.dbItems[0] || {}, tempProduct);
        tempProduct = mergeObjects(catalogSearch.ctItems[0] || {}, tempProduct);
      } catch (error) {
        console.error(`Failed to fetch from post_catalog_search for Item Id: ${product.itemId}:`, error);
      }

      setCurrProduct(tempProduct);
      setScrapeLoading(false);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const getStep = () => {
    if (activeStep === 0) {
      return (
        <Box sx={{ textAlign: 'center' }}>
          <NewBatchModal
            open={newBatchOpen} setOpen={setNewBatchOpen}
            batchName={batchName} setBatchName={setBatchName}
            channel={channel} setChannel={setChannel}
            handleNext={handleNext}
          />
        </Box>
      )
    } else if (activeStep === 1) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: "center",
            textAlign: "center",
            width: { xs: "90vw", sm: "75vw", md: "60vw", lg: "50vw" },
            height: { xs: "auto", md: "50vh", lg: "60vh" },
            m: "auto",
            backgroundColor: "rgba(200,200,200,0.2)",
            borderRadius: "30px",
            mt: "3vh",
            py: "2vh",
            px: "1vh",
          }}
        >
          {identifier === "" ? (
            <>
              <Typography
                sx={{
                  fontSize: { xs: "120%", sm: "130%", md: "150%" },
                  marginBottom: "2vh",
                  fontFamily: theme.fontFamily,
                  color: theme.text.primary,
                }}
              >
                Select the identifier for product matching
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-around',
                  alignItems: "center",
                  width: "75%",
                }}
              >
                <Typography sx={styles.identifier} onClick={() => setIdentifier("Item ID")}>Item ID</Typography>
                <Typography sx={styles.identifier} onClick={() => setIdentifier("Barcode")}>UPC \ EAN</Typography>
              </Box>
            </>
          ) : (
            <>
              <Typography
                variant="h5"
                sx={{
                  color: theme.text.primary,
                  mb: "2vh",
                  fontFamily: theme.fontFamily,
                  fontWeight: "bold",
                  fontSize: { xs: "100%", sm: "120%", md: "140%" },
                }}
              >
                Upload the CSV file
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  color: theme.text.primary,
                  fontFamily: theme.fontFamily,
                  mb: 2,
                  fontWeight: 600,
                  fontSize: { xs: "80%", sm: "90%", md: "100%" },
                }}
              >
                The file should include the following:
              </Typography>

              <Box sx={{ mt: 1 }}>
                <Grid container spacing={1}>
                  <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <Box sx={styles.optionBox}>
                      <CodeIcon sx={styles.optionIcon} />
                      <Typography sx={styles.optionText}>
                        {identifier}{" "}
                        <Typography component="span" sx={{ ...styles.optionTextOptional, color: theme.accent.error }}>
                          (required)
                        </Typography>
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <Box sx={styles.optionBox}>
                      <AttachMoneyIcon sx={styles.optionIcon} />
                      <Typography sx={styles.optionText}>
                        Cost Price{" "}
                        <Typography component="span" sx={{ ...styles.optionTextOptional, color: theme.accent.error }}>
                          (required)
                        </Typography>
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <Box sx={styles.optionBox}>
                      <LabelIcon sx={styles.optionIcon} />
                      <Typography sx={styles.optionText}>
                        Name{" "}
                        <Typography component="span" sx={styles.optionTextOptional}>
                          (optional)
                        </Typography>
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <Box sx={styles.optionBox}>
                      <ImageIcon sx={styles.optionIcon} />
                      <Typography sx={styles.optionText}>
                        Image{" "}
                        <Typography component="span" sx={styles.optionTextOptional}>
                          (optional)
                        </Typography>
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <Box sx={styles.optionBox}>
                      <SourceIcon sx={styles.optionIcon} />
                      <Typography sx={styles.optionText}>
                        Source{" "}
                        <Typography component="span" sx={styles.optionTextOptional}>
                          (optional)
                        </Typography>
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <Box sx={styles.optionBox}>
                      <AbcIcon sx={styles.optionIcon} />
                      <Typography sx={styles.optionText}>
                        SKU{" "}
                        <Typography component="span" sx={styles.optionTextOptional}>
                          (optional)
                        </Typography>
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <Box sx={styles.optionBox}>
                      <AttachMoneyIcon sx={styles.optionIcon} />
                      <Typography sx={styles.optionText}>
                        Sell Price{" "}
                        <Typography component="span" sx={styles.optionTextOptional}>
                          (optional)
                        </Typography>
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <Box sx={styles.optionBox}>
                      <CalendarMonthIcon sx={styles.optionIcon} />
                      <Typography sx={styles.optionText}>
                        Expiration Date{" "}
                        <Typography component="span" sx={styles.optionTextOptional}>
                          (optional)
                        </Typography>
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <Box sx={styles.optionBox}>
                      <CalendarMonthIcon sx={styles.optionIcon} />
                      <Typography sx={styles.optionText}>
                        Purchase Date{" "}
                        <Typography component="span" sx={styles.optionTextOptional}>
                          (optional)
                        </Typography>
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <Box sx={styles.optionBox}>
                      <TagIcon sx={styles.optionIcon} />
                      <Typography sx={styles.optionText}>
                        Quantity{" "}
                        <Typography component="span" sx={styles.optionTextOptional}>
                          (optional)
                        </Typography>
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <Box sx={styles.optionBox}>
                      <NoteIcon sx={styles.optionIcon} />
                      <Typography sx={styles.optionText}>
                        Notes{" "}
                        <Typography component="span" sx={styles.optionTextOptional}>
                          (optional)
                        </Typography>
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Typography
                variant="h6"
                sx={{
                  color: theme.text.primary,
                  mb: "2vh",
                  fontFamily: theme.fontFamily,
                  fontSize: { xs: "100%", sm: "110%" },
                }}
              >
                Make sure you use our template or use the same column names!
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "slateblue",
                  mb: "2vh",
                  fontFamily: theme.fontFamily,
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontSize: { xs: "90%", sm: "100%" },
                }}
                onClick={() => {
                  const csvContent = `data:text/csv;charset=utf-8,${identifier},Cost Price,Name,Image,Source,SKU,Sell Price,Expiration Date,Purchase Date,Quantity,Notes\n`;
                  const encodedUri = encodeURI(csvContent);

                  const link = document.createElement("a");
                  link.href = encodedUri;
                  link.download = "template.csv";
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                Click here to download the template
              </Typography>

              <Box
                sx={{
                  border: '2px dashed #ccc',
                  padding: '16px',
                  textAlign: 'center',
                  width: { xs: "80%", sm: "50%", md: "50%" },
                  borderRadius: '8px',
                  cursor: 'pointer',
                  margin: '15px auto',
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<CloudUpload />}
                  size="large"
                  sx={{
                    textTransform: 'none',
                    fontFamily: theme.fontFamily,
                    color: theme.text.negative,
                    borderRadius: '10px',
                    backgroundColor: theme.color.primary,
                    border: `1px solid ${theme.border.light}`,
                    fontSize: { xs: "90%", sm: "100%" },
                  }}
                  onClick={handleFileButtonClick}
                  disabled={isLoadingUploadFile}
                >
                  Choose a file
                </Button>

                <VisuallyHiddenInput ref={fileInputRef} type="file" onChange={handleFileChange} accept=".csv,.xlsx" />

                <Typography
                  variant="body2"
                  sx={{
                    mt: '8px',
                    fontFamily: theme.fontFamily,
                    color: theme.text.primary,
                    fontSize: { xs: "90%", sm: "100%" },
                  }}
                >
                  (Accept .csv and .xlsx files)
                </Typography>

                {isLoadingUploadFile ? (
                  <CustomLinearProgress />
                ) : (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      marginTop: '8px',
                      fontFamily: theme.fontFamily,
                      color: theme.text.primary,
                      fontSize: { xs: "80%", sm: "90%" },
                    }}
                  >
                    No file uploaded
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Box>
      );
    } else if (activeStep === 2) {
      return (
        <>
          {scrapeLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', }}>
              <CustomCircularProgress />
            </Box>
          ) : (
            <SearchedProductsTable products={csvItems} setCurrProduct={setCurrProduct} isLoading={isLoading} handleNext={handleNext} />
          )}
        </>
      )
    } else if (activeStep === 3) {
      return (
        <>
          {channel === "wfs" ? (
            <Form currProduct={currProduct} barcode={currProduct.Barcode || currProduct.upc} handleAddItem={handleAddItem} batchName={batchName} skuUpdate={skuUpdate} setSkuUpdate={setSkuUpdate} />
          ) : (
            <FormSF currProduct={currProduct} barcode={currProduct.Barcode || currProduct.upc} handleAddItem={handleAddItem} batchName={batchName} />
          )}
        </>
      )
    }
  };

  const changeQty = async (value, item) => {
    if (value === "") {
      return;
    }
    try {
      const body = {
        sku: item.sku,
        email: user,
        qty: parseInt(value)
      }
      item.qty = parseInt(value);
      fetchWrapperNoAuth(`${SERVER}/falcon_update_qty`, user, password, { method: 'POST', body: body });
    } catch (error) {
      console.error('Error update batch:', error.message);
    }
  };

  // Items Proccess Pipeline
  const processItems = async () => {  // filter to get only LOADING items
    const loadingItems = addedItems.filter((item) => ["LOADING", "TIME_BRAKE"].includes(item.status));
    const restItems = addedItems.filter((item) => !["LOADING", "TIME_BRAKE"].includes(item.status));

    const updatedLoadingItems = await Promise.all(
      loadingItems.map(async (item) => {
        try {
          let updatedItem;
          if ((item.isWfs || channel === "wfs") && !item.skuUpdate) {
            if (item.step === 1) {          // Check feed status (needs to be PROCESSED / RECEIVED)
              updatedItem = await checkFeed(item, user, password, channel, "CsvUpload.js", encodedAuth);
            } else if (item.step === 2) {   // Check published status (needs to be PUBLISHED / READY_TO_PUBLISH / IN_PROGRESS)
              updatedItem = await checkItem(item, user, password, channel, "CsvUpload.js", encodedAuth);
            } else if (item.step === 3) {   // Convert item to WFS (using the item.scheme) and refresh the item.feedId
              updatedItem = await convertItemWFS(item, user, password, channel, "CsvUpload.js", encodedAuth);
            } else if (item.step === 4) {   // Check feed status (needs to be PROCESSED / RECEIVED) after convert to WFS
              updatedItem = await checkFeed(item, user, password, channel, "CsvUpload.js", encodedAuth);
            } else if (item.step === 5) {   // Check published status (needs to be PUBLISHED / READY_TO_PUBLISH / IN_PROGRESS) after convert to WFS
              updatedItem = await checkItem(item, user, password, channel, "CsvUpload.js", encodedAuth);
            } else if (item.step === 6) {
              updatedItem = {
                ...item,
                status: (item.publishedStatus === "PUBLISHED" && item.feedStatus !== "ERROR") ? "SUCCESS" : "ERROR",
                stepRetryCount: 0 // Reset for future use
              };
              if (item.publishedStatus === "PUBLISHED" && item.feedStatus !== "ERROR" && isSubUser) {
                addToSentItemsLS(user, item);
              }
            } else {
              updatedItem = item;
            }
          } else {
            if (item.step === 1) {          // Check feed status (needs to be PROCESSED / RECEIVED)
              updatedItem = await checkFeed(item, user, password, channel, "CsvUpload.js", encodedAuth);
            } else if (item.step === 2) {   // Check published status (needs to be PUBLISHED / READY_TO_PUBLISH / IN_PROGRESS)
              updatedItem = await checkItem(item, user, password, channel, "CsvUpload.js", encodedAuth);
            } else if (item.step === 3) {
              updatedItem = {
                ...item,
                status: (item.publishedStatus === "PUBLISHED" && item.feedStatus !== "ERROR") ? "SUCCESS" : "ERROR",
                step: 6,
                stepRetryCount: 0 // Reset for future use
              };
              if (item.publishedStatus === "PUBLISHED" && item.feedStatus !== "ERROR" && isSubUser) {
                addToSentItemsLS(user, item);
              }
            } else {
              updatedItem = item;
            }
          }

          return updatedItem;
        } catch (error) {
          // If anything fails, check if we should retry this step
          console.error(error);

          const stepRetryCount = (item.stepRetryCount || 0) + 1;

          // If we haven't reached max retries for this step, don't send error message yet
          if (stepRetryCount < MAX_STEP_RETRIES) {
            console.log(`Retrying step ${item.step} for item ${item.sku} (attempt ${stepRetryCount} of ${MAX_STEP_RETRIES})`);
            return {
              ...item,
              stepRetryCount,
              lastRequest: new Date().toISOString()
            };
          }

          // If we've reached max retries, send error message and update status
          sendErrorMessage(user, password, channel, item.itemId, item.upc, "CsvUpload.js", item.step, "processItems", "ERROR", [error.toString()]);
          return {
            ...item,
            status: 'ERROR',
            lastRequest: new Date().toISOString(),
            stepRetryCount: 0 // Reset for future use
          };
        }
      })
    );

    if (updatedLoadingItems.filter(item => item.status === "SUCCESS").length > successedItems) {
      handleSaveBatches();
    }

    setSuccessedItems(updatedLoadingItems.filter(item => item.status === "SUCCESS").length);
    setAddedItems([...restItems, ...updatedLoadingItems]);
  };

  useEffect(() => {
    const interval = setInterval(processItems, 10000);
    return () => clearInterval(interval);
  }, [addedItems]);

  useEffect(() => {
    if (addedItems.length > 0) {
      processItems();
    }
  }, [addedItems.length]);


  const handleTransferToAddItems = () => {
    if (addedItems.length < 1) return;

    const stripedName = batchName.replace(/\.csv$|\.xlsx$/i, '');
    const addItemStorage = JSON.parse(localStorage.getItem("IFAddItemProccess") ?? "{}");

    const existingBatch = addItemStorage[stripedName];

    if (existingBatch) {
      const existingItemIds = new Set(existingBatch.addedItems.map(item => item.itemId));
      const newItems = addedItems.filter(item => !existingItemIds.has(item.itemId));

      existingBatch.addedItems = [...existingBatch.addedItems, ...newItems];
    } else {
      addItemStorage[stripedName] = {
        addedItems: addedItems.map(cleanItemMetadataToPreventQuotaExceeded),
        searchedItems: [],
        channel: channel,
      }
    }
    localStorage.setItem("IFAddItemProccess", JSON.stringify(addItemStorage));
    alertError("Transferred Batch Successfully!")
  }

  const styles = {
    root: {
      display: 'flex',
      height: '100%',
      bgcolor: theme.background.primary,
    },
    mainContent: {
      flex: 1,
      p: 3,
      overflow: 'auto'
    },
    itemsList: {
      flex: 1,
      overflowY: 'auto',
      marginRight: '5px',
      '&::-webkit-scrollbar': {
        width: '10px', // Width of the scrollbar
        height: '10px', // Width of the scrollbar
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: theme.background.primary, // Track color
        borderRadius: '15px', // Optional: rounded corners
        border: `1px solid ${theme.border.light}`,
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.scrollbar, // Thumb color
        borderRadius: '15px', // Optional: rounded corners
        cursor: 'grab',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: theme.scrollbarHover, // Thumb color on hover
      },
      '&::-webkit-scrollbar-button': {
        display: 'none', // Thumb color on hover
      },
    },
    buttonContainer: {
      p: 2,
      borderTop: `1px solid ${theme.border.medium}`,
      bgcolor: theme.background.secondary,
    },
    navigationButton: {
      width: '100%',
      mb: 1,
      textTransform: 'none',
      fontFamily: theme.fontFamily,
      border: `1px solid ${theme.border.light}`,
      borderRadius: '10px',
      color: theme.text.negative,
      backgroundColor: theme.color.primary,
    },
    identifier: {
      fontFamily: theme.fontFamily,
      color: theme.color.primary,
      p: '25px',
      backgroundColor: theme.background.primary,
      borderRadius: "10px",
      border: `1px solid ${theme.border.light}`,
      cursor: 'pointer',
    },
    optionBox: {
      display: 'flex',
      alignItems: 'center',
      mb: 1.5,
      bgcolor: `${theme.color.secondary}15`,
      p: 1,
      borderRadius: '20px',
    },
    optionText: {
      fontFamily: theme.fontFamily,
      color: theme.text.primary,
      fontSize: {
        xs: "80%",
        sm: "90%",
      },
      cursor: 'default'
    },
    optionIcon: {
      mr: 1,
      color: theme.color.primary,
    },
    optionTextOptional: {
      ml: 0.5,
      fontFamily: theme.fontFamily,
      color: theme.text.tertiary,
      fontSize: {
        xs: "70%",
        sm: "80%",
      },
    },
  };

  if (!encodedAuth || encodedAuth === "") {
    alert("Please enter you credentials (Client Id & Client Secret from Walmart)");
    navigate('/account');
  }

  return (
    <Box sx={styles.root}>
      {/* Main Content Area */}
      <Box sx={styles.mainContent}>
        <Typography variant="h5" sx={{ textAlign: 'center', mb: 3, fontFamily: theme.fontFamily, color: theme.text.primary }}>
          Add Items from CSV file
        </Typography>

        {/* Stepper */}
        <CustomStepper activeStep={activeStep} steps={steps} />

        {/* Page actions buttons */}
        {activeStep === 2 && !scrapeLoading && (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-evenly", mt: 2 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              sx={{
                py: 0.5,
                fontFamily: theme.fontFamily,
                backgroundColor: theme.color.primary,
                borderRadius: '20px',
                textTransform: "none",
                '&.Mui-disabled': {
                  opacity: 0.5,
                  color: theme.text.negative,
                  backgroundColor: theme.accent.disabled,
                }
              }}
              onClick={() => { setIsReplen(true); setOpenSearchCatalog(true); }}
            >
              Add item from catalog to batch
            </Button>

            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              sx={{
                py: 0.5,
                fontFamily: theme.fontFamily,
                backgroundColor: theme.color.primary,
                borderRadius: '20px',
                textTransform: "none",
                '&.Mui-disabled': {
                  opacity: 0.5,
                  color: theme.text.negative,
                  backgroundColor: theme.accent.disabled,
                }
              }}
              onClick={() => { setIsReplen(false); setOpenSearchCatalog(true); }}
            >
              Add New Item to batch
            </Button>
          </Box>
        )}

        {/* Main form content */}
        <Box sx={{ mt: 2, }}>
          {getStep()}
        </Box>
      </Box>

      {/* Sidebar */}
      <ResizableSidebar width={sidebarWidth} setWidth={setSidebarWidth}>
        {/* Added Items List */}
        <Box sx={{ p: 2, borderBottom: `1px solid ${theme.border.primary}`, color: theme.text.primary, }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography
              variant="h6"
              sx={{ cursor: 'pointer', fontFamily: theme.fontFamily }}
              onClick={() => { if (activeStep === 0) setNewBatchOpen(true) }}
            >
              {batchName !== "none" && batchName}
            </Typography>
            {batchName !== "none" && addedItems.length > 0 && (
              <HtmlTooltip
                customtheme={theme}
                title={
                  <Typography sx={{ color: theme.text.primary, fontFamily: theme.fontFamily, fontSize: 12 }}>
                    Transfer batch to Add Items
                  </Typography>
                }
                arrow
                placement={"bottom"}
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, -15],
                        },
                      },
                    ],
                  },
                }}
              >
                <IconButton onClick={handleTransferToAddItems}>
                  <LaunchIcon fontSize="small" sx={{ color: theme.text.primary }} />
                </IconButton>
              </HtmlTooltip>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontFamily: theme.fontFamily }}>
              Added Items ({addedItems.length})
            </Typography>
            <Box sx={{ display: 'flex' }}>
              {addedItems.length > 0 && <>
                {loadingBarcode ? <>
                  <CustomCircularProgress />
                </> : <>
                  <HtmlTooltip
                    customtheme={theme}
                    title={
                      <Typography sx={{ color: theme.text.primary, fontFamily: theme.fontFamily, fontSize: 12 }}>
                        Click to download Barcode labels
                      </Typography>
                    }
                    arrow
                    placement={batchName !== "" ? "top" : "bottom"}
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, -15],
                            },
                          },
                        ],
                      },
                    }}
                  >
                    <IconButton
                      onClick={() => setOpenMultipleBarcodeModal(true)}
                      sx={{ color: theme.text.primary, }}
                    >
                      <QrCodeIcon />
                    </IconButton>
                  </HtmlTooltip>
                </>}
              </>}
              <HtmlTooltip
                customtheme={theme}
                title={
                  <Typography sx={{ color: theme.text.primary, fontFamily: theme.fontFamily, fontSize: 12 }}>
                    Save batches to the cloud
                  </Typography>
                }
                arrow
                placement={batchName !== "" ? "top" : "bottom"}
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, -15],
                        },
                      },
                    ],
                  },
                }}
              >
                <IconButton
                  onClick={handleSaveBatches}
                  sx={{ color: theme.accent.success, }}
                >
                  <CloudUpload />
                </IconButton>
              </HtmlTooltip>
              <HtmlTooltip
                customtheme={theme}
                title={
                  <Typography sx={{ color: theme.text.primary, fontFamily: theme.fontFamily, fontSize: 12 }}>
                    Load batches from the cloud
                  </Typography>
                }
                arrow
                placement={batchName !== "" ? "top" : "bottom"}
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, -15],
                        },
                      },
                    ],
                  },
                }}
              >
                <IconButton
                  onClick={handleLoadBatches}
                  sx={{ color: theme.color.primary, }}
                >
                  <CloudDownload />
                </IconButton>
              </HtmlTooltip>
            </Box>
          </Box>
        </Box>

        <Box sx={styles.itemsList}>
          {addedItems.map((item, index) => (
            <ResponsiveItemCard
              key={index}
              item={item}
              index={index}
              theme={theme}
              onRetry={retryAddItem}
              changeQty={changeQty}
              editItem={editItem}
              setItemToModify={setItemToModify}
              setOpenDeleteFromBatchModal={setOpenDeleteFromBatchModal}
              setBarcodeLabelProduct={setBarcodeLabelProduct}
              setOpenBarcodeModal={setOpenBarcodeModal}
              sidebarWidth={sidebarWidth}
              isWfs={channel === "wfs"}
            />
          ))}
        </Box>

        <Box sx={{ ml: '10px', mt: '10px' }}>
          {(() => {
            const itemsToSum = addedItems.filter(item => item.publishedStatus === "PUBLISHED")
            const sellPrice = itemsToSum.reduce((sum, item) => sum + ((item.price?.amount ?? 0) * (item.qty || 1)), 0);
            const costPrice = itemsToSum.reduce((sum, item) => sum + ((item.cost || 0) * (item.qty || 1)), 0);
            const totalRefferalFees = itemsToSum.reduce((sum, item) => sum + ((calcRefFees(item)) * (item.qty || 1)), 0);
            const totalWfsCost = itemsToSum.reduce((sum, item) => sum + ((calcWfs(item) + (((item.price?.amount ?? 0) && (item.price?.amount ?? 0) < 10) ? 1 : 0)) * (item.qty || 1)), 0);
            const feesPrice = totalRefferalFees + totalWfsCost;
            const profit = sellPrice - costPrice - feesPrice;
            const totalUnits = itemsToSum.reduce((sum, item) => sum + (item.qty || 1), 0);

            return (
              <>
                <Typography variant="body2" sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, }}>
                  Total units: {totalUnits}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, }}>
                  Total sell price: ${sellPrice.toFixed(2)}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, }}>
                  Total cost price: ${costPrice.toFixed(2)}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: theme.fontFamily, color: profit > 0 ? theme.accent.success : theme.accent.error }}>
                  Profit: {profit < 0 && "-"}${profit < 0 ? (profit * -1).toFixed(2) : profit.toFixed(2)}
                </Typography>
              </>
            )
          })()}
        </Box>

        {/* Navigation Buttons */}
        <Box sx={styles.buttonContainer}>
          {activeStep === 0 && !batchName && (
            <Button
              variant="contained"
              sx={{
                ...styles.navigationButton,
                backgroundColor: theme.accent.success,
              }}
              onClick={() => setNewBatchOpen(true)}
            >
              Create New Batch
            </Button>
          )}
          <Button
            variant="outlined"
            sx={{
              ...styles.navigationButton,
              color: theme.color.primary,
              backgroundColor: theme.background.primary,
              "&.Mui-disabled": {
                color: 'gray',
              },
            }}
            onClick={handleBack}
            startIcon={<ArrowBackIcon />}
            disabled={activeStep === 0}
          >
            {activeStep === 1 ? "Change Batch Name" : "Previous Step"}
          </Button>

          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              sx={styles.navigationButton}
              onClick={handleAddAnotherItem}
              startIcon={<AddIcon />}
            >
              Add Another Item
            </Button>
          ) : (
            <Button
              variant="contained"
              sx={styles.navigationButton}
              onClick={() => handleNext(currProduct)}
              endIcon={<ArrowForwardIcon />}
            >
              Next Step
            </Button>
          )}
          <Button
            variant="contained"
            sx={styles.navigationButton}
            onClick={handleSendAllItems}
            startIcon={<SendIcon />}
          >
            {channel === "sf" ? "Add" : "Send"} All Items ({addedItems.filter(item => item.status === "SUCCESS").length})
          </Button>
        </Box>
      </ResizableSidebar>

      <Snackbar
        open={alertBatch.open}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}  // Position at the top-center
        autoHideDuration={3000}
        onClose={() => setAlertBatch({ ...alertBatch, open: false })}
        sx={{ marginTop: props.navbarHeight }}  // Add margin of 10vh from the top
      >
        <Alert severity={alertBatch.severity} sx={{ width: "100%", zIndex: 999, fontFamily: theme.fontFamily }}>
          {alertBatch.message}
        </Alert>
      </Snackbar>

      <QuestionModal
        open={openDeleteFromBatchModal}
        setOpen={setOpenDeleteFromBatchModal}
        message={
          <Box>
            <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, fontSize: '16px', }}>This item will be removed only from your batch, not from your inventory.</Typography>
            <br />
            <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, fontSize: '16px', fontWeight: 'bold', }}>Are you sure you want to proceed?</Typography>
          </Box>
        }
        confirm={() => { removeFromBatch(itemToModify); setOpenDeleteFromBatchModal(false); }}
        cancel={() => setOpenDeleteFromBatchModal(false)}
      />

      <BatchSelectionModal
        open={openConfirmModal}
        setOpen={setOpenConfirmModal}
        batchesArray={batchesArray}
        handleSelectBatch={(batchSelected) => loadStatesFromLocalStorage(batchSelected)}
        removeBatch={(batchSelected) => clearStoredData(batchSelected)}
        cancel={() => { setLoadStates(true); setOpenConfirmModal(false); }}
      />

      <BarcodeLabelModal open={openBarcodeModal} setOpen={setOpenBarcodeModal} product={barcodeLabelProduct} />
      <BarcodeMultipleLabelsModal open={openMultipleBarcodeModal} setOpen={setOpenMultipleBarcodeModal} items={addedItems} setLoading={setLoadingBarcode} />

      <ErrorModal open={openErrorModal} setOpen={setopenErrorModal} message={errorMessage} />

      <ShippmentsModal open={shippmentOpenModal} setOpen={setShippmentOpenModal} items={addedItems.filter(item => item.status === "SUCCESS")} clearStoredData={() => clearStoredData(batchName)} />
      <SearchCatalogModal
        open={openSearchCatalog}
        onClose={() => setOpenSearchCatalog(false)}
        setCurrProduct={setCurrProduct}
        handleNext={!isReplen ? handleNext : handleAddItemReplen}
        prefetchItemData={prefetchItemData}
        isReplen={isReplen}
      />
    </Box>
  );
}

export default CsvUpload;
